/** @jsxImportSource @emotion/react */
import { ChangeEvent, useEffect, useState } from 'react';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { css } from '@emotion/react';
import { FormFieldConfig } from '../../Hooks/useForm';
import { getGuid } from '../../Utils/stringUtils';
import './biz-checkbox.scss';

const checkBoxCss = css`
	padding: 0;
`;

const getLabelCss = (showDescription: boolean) => css`
	margin-left: 0;
	align-items: ${showDescription ? 'start' : 'center'};

	.biz-checkbox-label-wrapper .biz-checkbox-label {
		display: block;
	}

	.biz-checkbox-label-wrapper .biz-checkbox-description {
		font-size: 11px;
		font-style: italic;
	}
`;

type BizCheckBoxProps = {
	onChange: (checked: boolean) => void;
	value: boolean;
	label?: string;
	description?: string;
	formConfig?: FormFieldConfig;
	disabled?: boolean;
	labelPlacement?: 'top' | 'bottom' | 'start' | 'end';
};

export function BizCheckBox({ onChange, value, label, description, formConfig, disabled, labelPlacement }: BizCheckBoxProps) {
	if (!label) {
		return <BaseCheckBox onChange={onChange} value={value} formConfig={formConfig} disabled={disabled} />;
	}

	if (!description) {
		return (
			<FormControlLabel
				className='biz-checkbox'
				css={getLabelCss(false)}
				label={label}
				labelPlacement={labelPlacement}
				control={<BaseCheckBox onChange={onChange} value={value} formConfig={formConfig} />}
				disabled={disabled}
			/>
		);
	}

	return (
		<FormControlLabel
			className='biz-checkbox'
			css={getLabelCss(true)}
			labelPlacement={labelPlacement}
			label={
				<div className='biz-checkbox-label-wrapper'>
					<span className='biz-checkbox-label'>{label}</span>
					<span className='biz-checkbox-description'>{description}</span>
				</div>
			}
			control={<BaseCheckBox onChange={onChange} value={value} formConfig={formConfig} />}
			disabled={disabled}
		/>
	);
}

function BaseCheckBox({ onChange, value, formConfig, disabled }: BizCheckBoxProps) {
	function changeHandler(e: ChangeEvent<HTMLInputElement>) {
		const newCheckedValue = e.target.checked;
		onChange(newCheckedValue);

		if (formConfig?.state && !formConfig.state.isDirty) {
			formConfig.state.setDirty();
		}
	}

	return <Checkbox css={checkBoxCss} checked={value} onChange={changeHandler} disabled={disabled} />;
}

type BizCheckBoxListProps<OptionType> = {
	name?: string;
	options: OptionType[];
	checkedOptions: OptionType[];
	onChange: (checkedOptions: OptionType[]) => void;
	className?: string;
	showDescription?: boolean;
	formConfig?: FormFieldConfig<unknown[]>;
	disabled?: boolean;
	getLabel?: (option: OptionType) => string;
};

export function BizCheckBoxList<OptionType extends { Id: number | string; Name: string; Description?: string }>({
	name: pName,
	options,
	checkedOptions,
	onChange,
	className,
	showDescription,
	formConfig,
	disabled,
	getLabel,
}: BizCheckBoxListProps<OptionType>) {
	const [name] = useState(pName ?? getGuid());
	const isValid = formConfig?.state.fields[name]?.isValid ?? true;

	useEffect(() => {
		if (!formConfig || !formConfig.validators) return;
		const newIsValid = formConfig.state.validateFieldValue(checkedOptions, formConfig.validators);

		if (newIsValid !== isValid) {
			formConfig.state.setFieldValid(name, newIsValid, formConfig.group);
		}
	}, [checkedOptions, formConfig?.state.formStateId, formConfig?.validators]);

	function changeHandler(option: OptionType, checked: boolean) {
		if (formConfig?.state && !formConfig.state.isDirty) {
			formConfig.state.setDirty();
		}
		const newCheckedOptions = checked ? [...checkedOptions, option] : checkedOptions.filter((o) => o.Id !== option.Id);
		onChange(newCheckedOptions);
	}

	function getOptionLabel(option: OptionType) {
		return getLabel ? getLabel(option) : option.Name;
	}

	let currentClasses = className ?? '';
	if (!isValid) {
		currentClasses += ' invalid';
	}

	return (
		<div className={`biz-checkbox-list ${currentClasses}`}>
			{options.map((option) => (
				<BizCheckBox
					key={option.Id}
					label={getOptionLabel(option)}
					description={showDescription ? option.Description : undefined}
					value={checkedOptions !== undefined && checkedOptions.some((o) => o.Id === option.Id)}
					onChange={(checked) => {
						changeHandler(option, checked);
					}}
					disabled={disabled}
				/>
			))}
		</div>
	);
}
